import { createSlice } from '@reduxjs/toolkit';
import { apiCallBegan } from './middleware/apiMiddleware';
import { getImageUrl } from '../utils/appUtils';

/**
 * USER SLICE - Authentication State Management
 * Pattern: Follows middleware-based workflow with centralized API calls
 *
 * Each operation (signup, login, forgot-password) has separate loading/error states
 * and uses middleware for centralized API handling via apiCallBegan action
 */
const initialState = {
    // User data
    user: null,
    
    // Auth token - stored in Redux for easy access
    authToken: null,
    
    // User's country - stored after login
    country: null,

    // Persisted profile image (resolved URL)
    profileImage: localStorage.getItem('profileImage') || null,

    // Profile image update states
    updateProfileLoading: false,
    updateProfileError: null,
    updateProfileSuccess: false,

    // Signup operation states
    signupLoading: false,
    signupError: null,
    signupSuccess: false,

    // Login operation states
    loginLoading: false,
    loginError: null,
    loginSuccess: false,

    // Forgot Password operation states
    forgotPasswordLoading: false,
    forgotPasswordError: null,
    forgotPasswordSuccess: false,

    // Verify User operation states (for forgot password step 1)
    verifyUserLoading: false,
    verifyUserError: null,
    verifyUserSuccess: false,
    verifiedUserData: null, // Stores { type, value } after successful verification

    // Send OTP operation states
    sendOtpLoading: false,
    sendOtpError: null,
    sendOtpSuccess: false,
    pendingVerificationEmail: null, // Stores email for OTP verification page

    // Verify OTP operation states
    verifyOtpLoading: false,
    verifyOtpError: null,
    verifyOtpSuccess: false,

    // Profile address (user location) states
    userAddress: null,
    addressLoading: false,
    addressSaveLoading: false,
    addressSaveSuccess: false,
    addressSaveError: null,
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        // ============ SIGNUP REDUCERS ============
        beginSignupLoader: (state) => {
            state.signupLoading = true;
            state.signupError = null;
        },
        setSignupData: (state, action) => {
            state.signupLoading = false;
            state.user = action.payload;
            state.signupError = null;
            state.signupSuccess = true;
            // Store token in Redux and localStorage
            if (action.payload?.token) {
                state.authToken = action.payload.token;
                localStorage.setItem('authToken', action.payload.token);
                localStorage.setItem('refreshToken', action.payload.refresh_token);
            }
            // Store country if available
            if (action.payload?.country) {
                state.country = action.payload.country;
                localStorage.setItem('userCountry', JSON.stringify(action.payload.country));
            }
        },
        signupError: (state, action) => {
            state.signupLoading = false;
            state.signupError = action.payload?.errorMessage || 'Signup failed';
            state.signupSuccess = false;
        },

        // ============ LOGIN REDUCERS ============
        beginLoginLoader: (state) => {
            state.loginLoading = true;
            state.loginError = null;
        },
        setLoginData: (state, action) => {
            state.loginLoading = false;
            state.user = action.payload;
            state.loginError = null;
            state.loginSuccess = true;
            // Store token in Redux and localStorage
            if (action.payload?.token) {
                state.authToken = action.payload.token;
                localStorage.setItem('authToken', action.payload.token);
                console.log('✅ Redux Store - authToken stored:', action.payload.token.substring(0, 30) + '...');
                localStorage.setItem('refreshToken', action.payload.refresh_token);
                console.log('✅ Redux Store - refreshToken stored:', action.payload.refresh_token.substring(0, 30) + '...');
            }
            // Store country if available
            if (action.payload?.country) {
                state.country = action.payload.country;
                localStorage.setItem('userCountry', JSON.stringify(action.payload.country));
                console.log('✅ Redux Store - country stored:', action.payload.country);
            }
            console.log('✅ Redux Store - Full user state:', { authToken: state.authToken ? 'Set' : 'Not set', country: state.country });
        },
        loginError: (state, action) => {
            state.loginLoading = false;
            state.loginError = action.payload?.errorMessage || 'Login failed';
            state.loginSuccess = false;
        },

        // ============ FORGOT PASSWORD REDUCERS ============
        beginForgotPasswordLoader: (state) => {
            state.forgotPasswordLoading = true;
            state.forgotPasswordError = null;
        },
        setForgotPasswordData: (state) => {
            state.forgotPasswordLoading = false;
            state.forgotPasswordError = null;
            state.forgotPasswordSuccess = true;
        },
        forgotPasswordError: (state, action) => {
            state.forgotPasswordLoading = false;
            state.forgotPasswordError = action.payload?.errorMessage || 'Forgot password request failed';
            state.forgotPasswordSuccess = false;
        },

        // ============ VERIFY USER REDUCERS ============
        beginVerifyUserLoader: (state) => {
            state.verifyUserLoading = true;
            state.verifyUserError = null;
        },
        setVerifyUserData: (state, action) => {
            state.verifyUserLoading = false;
            state.verifyUserError = null;
            state.verifyUserSuccess = true;
            // Store verified data for UpdatePassword page
            state.verifiedUserData = action.payload?.verifiedData || null;
        },
        verifyUserError: (state, action) => {
            state.verifyUserLoading = false;
            state.verifyUserError = action.payload?.errorMessage || 'User verification failed';
            state.verifyUserSuccess = false;
        },
        resetVerifyUserSuccess: (state) => {
            state.verifyUserSuccess = false;
        },
        clearVerifiedUserData: (state) => {
            state.verifiedUserData = null;
            state.verifyUserSuccess = false;
        },

        // ============ SEND OTP REDUCERS ============
        beginSendOtpLoader: (state) => {
            state.sendOtpLoading = true;
            state.sendOtpError = null;
        },
        setSendOtpData: (state, action) => {
            state.sendOtpLoading = false;
            state.sendOtpError = null;
            state.sendOtpSuccess = true;
            // Store email for OTP verification page
            state.pendingVerificationEmail = action.payload?.email || null;
        },
        sendOtpError: (state, action) => {
            state.sendOtpLoading = false;
            state.sendOtpError = action.payload?.errorMessage || 'Failed to send OTP';
            state.sendOtpSuccess = false;
        },
        resetSendOtpSuccess: (state) => {
            state.sendOtpSuccess = false;
        },

        // ============ VERIFY OTP REDUCERS ============
        beginVerifyOtpLoader: (state) => {
            state.verifyOtpLoading = true;
            state.verifyOtpError = null;
        },
        setVerifyOtpData: (state) => {
            state.verifyOtpLoading = false;
            state.verifyOtpError = null;
            state.verifyOtpSuccess = true;
            // Clear pending email after successful verification
            state.pendingVerificationEmail = null;
        },
        verifyOtpError: (state, action) => {
            state.verifyOtpLoading = false;
            state.verifyOtpError = action.payload?.errorMessage || 'Invalid OTP';
            state.verifyOtpSuccess = false;
        },
        resetVerifyOtpSuccess: (state) => {
            state.verifyOtpSuccess = false;
        },
        clearPendingVerificationEmail: (state) => {
            state.pendingVerificationEmail = null;
        },

        // ============ GENERAL REDUCERS ============
        clearUserData: (state) => {
            state.user = null;
            localStorage.removeItem('authToken');
        },
        clearSignupError: (state) => {
            state.signupError = null;
        },
        clearLoginError: (state) => {
            state.loginError = null;
        },
        clearForgotPasswordError: (state) => {
            state.forgotPasswordError = null;
        },

        // Reset success states (for cleanup after redirect)
        resetSignupSuccess: (state) => {
            state.signupSuccess = false;
        },
        resetLoginSuccess: (state) => {
            state.loginSuccess = false;
        },
        resetForgotPasswordSuccess: (state) => {
            state.forgotPasswordSuccess = false;
        },

        // ============ PROFILE IMAGE REDUCERS ============
        beginUpdateProfileLoader: (state) => {
            state.updateProfileLoading = true;
            state.updateProfileError = null;
            state.updateProfileSuccess = false;
        },
        setProfileImageLocal: (state, action) => {
            // Optimistic local preview (base64 or URL)
            state.profileImage = action.payload || null;
        },
        setProfileImageData: (state, action) => {
            state.updateProfileLoading = false;
            state.updateProfileError = null;
            state.updateProfileSuccess = true;

            const filePath = action.payload?.profile_image;
            if (filePath) {
                // Backend returns S3 key; frontend will resolve to full URL elsewhere if needed
                state.profileImage = filePath;
                localStorage.setItem('profileImage', filePath);
            }
        },
        updateProfileImageError: (state, action) => {
            state.updateProfileLoading = false;
            state.updateProfileError = action.payload?.errorMessage || 'Failed to update profile image';
            state.updateProfileSuccess = false;
        },

        // ============ PROFILE ADDRESS REDUCERS ============
        beginAddressLoader: (state) => {
            state.addressLoading = true;
            state.addressSaveError = null;
        },
        setUserAddress: (state, action) => {
            state.addressLoading = false;
            const p = action.payload;
            const profileImageFromAddress = p?.profile_image;
            if (profileImageFromAddress) {
                state.profileImage = profileImageFromAddress;
                localStorage.setItem('profileImage', profileImageFromAddress);
            }
            state.userAddress =
                p && (p.id != null || p.country_id != null) ? p : null;
        },
        addressError: (state, action) => {
            state.addressLoading = false;
            state.addressSaveError = action.payload?.errorMessage || 'Failed to fetch address';
        },
        beginAddressSaveLoader: (state) => {
            state.addressSaveLoading = true;
            state.addressSaveError = null;
            state.addressSaveSuccess = false;
        },
        setAddressSaveSuccess: (state, action) => {
            state.addressSaveLoading = false;
            state.addressSaveError = null;
            state.addressSaveSuccess = true;
            // Only update userAddress when backend returns full location (update path has "data")
            const location = action.payload?.data;
            if (location != null && (location.id != null || location.country_id != null)) {
                state.userAddress = location;
            }
        },
        addressSaveError: (state, action) => {
            state.addressSaveLoading = false;
            state.addressSaveError = action.payload?.errorMessage || 'Failed to save address';
            state.addressSaveSuccess = false;
        },
        resetAddressSaveSuccess: (state) => {
            state.addressSaveSuccess = false;
        },
    },
});

// ============ EXPORT ACTIONS ============
export const {
    beginSignupLoader,
    setSignupData,
    signupError,
    beginLoginLoader,
    setLoginData,
    loginError,
    beginForgotPasswordLoader,
    setForgotPasswordData,
    forgotPasswordError,
    beginVerifyUserLoader,
    setVerifyUserData,
    verifyUserError,
    resetVerifyUserSuccess,
    clearVerifiedUserData,
    beginSendOtpLoader,
    setSendOtpData,
    sendOtpError,
    resetSendOtpSuccess,
    beginVerifyOtpLoader,
    setVerifyOtpData,
    verifyOtpError,
    resetVerifyOtpSuccess,
    clearPendingVerificationEmail,
    clearUserData,
    clearSignupError,
    clearLoginError,
    clearForgotPasswordError,
    resetSignupSuccess,
    resetLoginSuccess,
    resetForgotPasswordSuccess,
    beginUpdateProfileLoader,
    setProfileImageLocal,
    setProfileImageData,
    updateProfileImageError,
    beginAddressLoader,
    setUserAddress,
    addressError,
    beginAddressSaveLoader,
    setAddressSaveSuccess,
    addressSaveError,
    resetAddressSaveSuccess,
} = userSlice.actions;

// ============ THUNK ACTIONS - Dispatch to middleware ============

/**
 * SIGNUP USER ACTION
 * Dispatches middleware action to handle signup API call
 * Pattern: Action → Thunk → Middleware → API Request → Reducer
 *
 * @param {Object} userData - { first_name, middle_name, last_name, email, mobile, password, user_role }
 */
export const signupUser = (userData) => (dispatch) => {
    const url = '/user';
    const method = 'POST';

    dispatch(
        apiCallBegan({
            url,
            method,
            data: userData,
            onStart: beginSignupLoader.type,
            onSuccess: setSignupData.type,
            onError: signupError.type,
            showGlobalError: true,
        })
    );
};

/**
 * LOGIN USER ACTION
 * Dispatches middleware action to handle login API call
 * Pattern: Action → Thunk → Middleware → API Request → Reducer
 *
 * @param {Object} credentials - { mobile: string, password: string }
 */
export const loginUser = (credentials) => (dispatch) => {
    const url = '/login';
    const method = 'POST';

    dispatch(
        apiCallBegan({
            url,
            method,
            data: credentials,
            onStart: beginLoginLoader.type,
            onSuccess: setLoginData.type,
            onError: loginError.type,
            showGlobalError: true,
        })
    );
};

/**
 * FORGOT PASSWORD USER ACTION
 * Dispatches middleware action to handle forgot password API call
 * Pattern: Action → Thunk → Middleware → API Request → Reducer
 *
 * @param {Object} payload - { mobile: string, password: string }
 */
export const forgotPasswordUser = (payload) => (dispatch) => {
    const url = '/forgot-password';
    const method = 'PUT';

    dispatch(
        apiCallBegan({
            url,
            method,
            data: payload,
            onStart: beginForgotPasswordLoader.type,
            onSuccess: setForgotPasswordData.type,
            onError: forgotPasswordError.type,
            showGlobalError: true,
        })
    );
};

/**
 * VERIFY USER ACTION
 * Dispatches middleware action to verify user exists (forgot password step 1)
 * Pattern: Action → Thunk → Middleware → API Request → Reducer
 *
 * @param {Object} payload - { type: "mobile"/"email", value: string }
 */
export const verifyUser = (payload) => (dispatch) => {
    const url = '/verify-user';
    const method = 'POST';

    dispatch(
        apiCallBegan({
            url,
            method,
            data: payload,
            onStart: beginVerifyUserLoader.type,
            onSuccess: setVerifyUserData.type,
            onError: verifyUserError.type,
            showGlobalError: true,
            // Pass along the verified data for storage
            meta: { verifiedData: payload },
        })
    );
};

/**
 * SEND OTP ACTION
 * Dispatches middleware action to send OTP to email.
 * GET request; captcha_token sent as query param.
 *
 * @param {string} otpType - Type of OTP (e.g., "signup")
 * @param {string} email - Email to send OTP to
 * @param {string} [captchaToken] - Turnstile token for captcha verification
 */
export const sendOtp = (otpType, email, captchaToken) => (dispatch) => {
    const url = `/send-otp/${otpType}/${encodeURIComponent(email)}`;
    const method = 'GET';
    const params = captchaToken ? { captcha_token: captchaToken } : undefined;

    dispatch(
        apiCallBegan({
            url,
            method,
            params,
            onStart: beginSendOtpLoader.type,
            onSuccess: setSendOtpData.type,
            onError: sendOtpError.type,
            showGlobalError: true,
            meta: { email },
        })
    );
};

/**
 * VERIFY OTP ACTION
 * Dispatches middleware action to verify OTP
 * Pattern: Action → Thunk → Middleware → API Request → Reducer
 *
 * @param {Object} payload - { otp_type, otp, email }
 */
export const verifyOtp = (payload) => (dispatch) => {
    const url = '/verify-otp';
    const method = 'POST';

    dispatch(
        apiCallBegan({
            url,
            method,
            data: payload,
            onStart: beginVerifyOtpLoader.type,
            onSuccess: setVerifyOtpData.type,
            onError: verifyOtpError.type,
            showGlobalError: true,
        })
    );
};

/**
 * UPDATE PROFILE IMAGE ACTION
 * Dispatches middleware action to handle profile image upload
 * Pattern: Action → Thunk → Middleware → API Request → Reducer
 *
 * @param {Object} imageData - { file: base64 string, extension: string, previewUrl?: string }
 */
export const updateProfileImage = (imageData) => (dispatch) => {
    const url = '/user/profile-image';
    const method = 'PUT';

    if (imageData.previewUrl) {
        dispatch(setProfileImageLocal(imageData.previewUrl));
    }

    dispatch(
        apiCallBegan({
            url,
            method,
            data: { file: imageData.file, file_extension: imageData.extension },
            onStart: beginUpdateProfileLoader.type,
            onSuccess: setProfileImageData.type,
            onError: updateProfileImageError.type,
            showGlobalError: true,
        })
    );
};

/**
 * FETCH USER ADDRESS ACTION
 * GET /get-user-location - loads saved profile address into userAddress
 */
export const fetchUserAddress = () => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: '/get-user-location',
            method: 'GET',
            onStart: beginAddressLoader.type,
            onSuccess: setUserAddress.type,
            onError: addressError.type,
            showGlobalError: false,
        })
    );
};

/**
 * SAVE USER ADDRESS ACTION
 * POST /update-user-location - create or update profile address
 * @param {Object} payload - { id?, user_id, country_id, state_id, city_id, address_line1?, address_line2?, pincode? }
 */
export const saveUserAddress = (payload) => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: '/update-user-location',
            method: 'POST',
            data: payload,
            onStart: beginAddressSaveLoader.type,
            onSuccess: setAddressSaveSuccess.type,
            onError: addressSaveError.type,
            showGlobalError: true,
        })
    );
};

// ============ SELECTORS - For component state access ============

/**
 * User Data Selectors
 */
export const selectUser = (state) => state.user.user;
export const selectUserId = (state) => state.user.user?.id;
export const selectUserEmail = (state) => state.user.user?.email;
export const selectUserName = (state) => state.user.user?.name;
export const selectIsUserLoggedIn = (state) => state.user.user !== null;

/**
 * Signup State Selectors
 */
export const selectSignupLoading = (state) => state.user.signupLoading;
export const selectSignupError = (state) => state.user.signupError;

/**
 * Login State Selectors
 */
export const selectLoginLoading = (state) => state.user.loginLoading;
export const selectLoginError = (state) => state.user.loginError;

/**
 * Forgot Password State Selectors
 */
export const selectForgotPasswordLoading = (state) => state.user.forgotPasswordLoading;
export const selectForgotPasswordError = (state) => state.user.forgotPasswordError;

/**
 * Success State Selectors
 */
export const selectSignupSuccess = (state) => state.user.signupSuccess;
export const selectLoginSuccess = (state) => state.user.loginSuccess;
export const selectForgotPasswordSuccess = (state) => state.user.forgotPasswordSuccess;
export const selectVerifyUserSuccess = (state) => state.user.verifyUserSuccess;
export const selectVerifyUserLoading = (state) => state.user.verifyUserLoading;
export const selectVerifyUserError = (state) => state.user.verifyUserError;
export const selectVerifiedUserData = (state) => state.user.verifiedUserData;

/**
 * OTP State Selectors
 */
export const selectSendOtpLoading = (state) => state.user.sendOtpLoading;
export const selectSendOtpSuccess = (state) => state.user.sendOtpSuccess;
export const selectSendOtpError = (state) => state.user.sendOtpError;
export const selectPendingVerificationEmail = (state) => state.user.pendingVerificationEmail;
export const selectVerifyOtpLoading = (state) => state.user.verifyOtpLoading;
export const selectVerifyOtpSuccess = (state) => state.user.verifyOtpSuccess;
export const selectVerifyOtpError = (state) => state.user.verifyOtpError;

/**
 * Combined Selectors
 */
export const selectIsAnyOperationLoading = (state) =>
    state.user.signupLoading || state.user.loginLoading || state.user.forgotPasswordLoading;

/**
 * Auth Token and Country Selectors
 */
export const selectAuthToken = (state) => state.user.authToken;
export const selectUserCountry = (state) => state.user.country;
export const selectProfileImage = (state) => {
    const value = state.user.profileImage;
    if (!value) return null;
    if (typeof value !== 'string') return null;
    if (value.startsWith('http') || value.startsWith('data:')) return value;
    return getImageUrl({ file_path: value });
};

/**
 * Profile address (user location) selectors
 */
export const selectUserAddress = (state) => state.user.userAddress;
export const selectAddressLoading = (state) => state.user.addressLoading;
export const selectAddressSaveLoading = (state) => state.user.addressSaveLoading;
export const selectAddressSaveSuccess = (state) => state.user.addressSaveSuccess;
export const selectAddressSaveError = (state) => state.user.addressSaveError;

export default userSlice.reducer;

